/**
 * useUserCountry — Single Source of Truth für das Country-Setting des Users.
 *
 * Reihenfolge der Auflösung:
 *   1. user.country (vom AuthContext)
 *   2. settings.country (falls geladen, im Settings-Context)
 *   3. localStorage 'ivoliq_country' (User-Override z.B. im Onboarding)
 *   4. Fallback 'DE'
 *
 * Verwendung:
 *   const { country, setCountry } = useUserCountry();
 *   formatCurrency(amount, country);
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isValidCountry } from '@/utils/i18nValidators';

const LS_KEY = 'ivoliq_country';

export function useUserCountry() {
  const { user } = useAuth();

  const resolve = useCallback(() => {
    if (user?.country && isValidCountry(user.country)) return user.country.toUpperCase();
    try {
      const ls = localStorage.getItem(LS_KEY);
      if (ls && isValidCountry(ls)) return ls.toUpperCase();
    } catch { /* SSR safe */ }
    return 'DE';
  }, [user]);

  const [country, setCountryState] = useState(resolve);

  useEffect(() => {
    setCountryState(resolve());
  }, [resolve]);

  const setCountry = useCallback((code) => {
    if (!isValidCountry(code)) return;
    const norm = code.toUpperCase();
    try { localStorage.setItem(LS_KEY, norm); } catch { /* noop */ }
    setCountryState(norm);
  }, []);

  return { country, setCountry };
}

export default useUserCountry;
